import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import {
  ChartLine,
  Plugs,
  Upload,
  MagnifyingGlass,
  Warning,
  Heartbeat,
  SignOut,
  List,
  X,
  Gear,
  Robot,
  Package,
  HandCoins,
  Handshake,
  ShieldCheck,
  Bell,
  Timer,
  Lightning,
} from '@phosphor-icons/react';

const navItems = [
  { path: '/', label: 'Sales Overview', icon: ChartLine, accent: 'lime' },
  { path: '/sales-decisions', label: 'Sales Decisions', icon: Lightning, accent: 'lime' },
  { path: '/integrations', label: 'Integrations', icon: Plugs, accent: 'blue' },
  { path: '/copilot', label: 'Copilot', icon: Robot, accent: 'orange' },
  { path: '/uploads', label: 'Transactions', icon: Upload, accent: 'lime' },
  { path: '/items', label: 'Sales by Items', icon: Package, accent: 'amber' },
  { path: '/explorer', label: 'Sales Explorer', icon: MagnifyingGlass, accent: 'cyan' },
  { path: '/service-charge', label: 'Service Charge', icon: HandCoins, accent: 'amber' },
  { path: '/discounts', label: 'Discounts', icon: Warning, accent: 'red' },
  { path: '/operations/table-efficiency', label: 'Table Efficiency', icon: Timer, accent: 'lime' },
  { path: '/partners', label: 'Partner Admin', icon: Handshake, accent: 'blue' },
  { path: '/confidence', label: 'Source Confidence', icon: ShieldCheck, accent: 'cyan' },
  { path: '/health', label: 'Source Health', icon: Heartbeat, accent: 'emerald' },
  { path: '/settings/outlet', label: 'Outlet Settings', icon: Gear, accent: 'emerald' }
];

const accentColors = {
  cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
  blue: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
  lime: 'text-[#D4FF00] bg-[#D4FF00]/10 border-[#D4FF00]/30',
  violet: 'text-violet-400 bg-violet-500/10 border-violet-500/30',
  red: 'text-red-400 bg-red-500/10 border-red-500/30',
  amber: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  orange: 'text-orange-400 bg-orange-500/10 border-orange-500/30'
};

const SEVERITY_STYLES = {
  CRITICAL: 'bg-red-500/10 border-red-500/30 text-red-400',
  HIGH: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
  MEDIUM: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
  LOW: 'bg-zinc-500/10 border-zinc-500/30 text-zinc-400',
  INFO: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
};

const AppLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [alertSummary, setAlertSummary] = useState(null);
  const [conflictSummary, setConflictSummary] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const fetchAlertSummary = useCallback(async () => {
    try {
      const res = await api.get('/source-confidence/alerts/summary');
      setAlertSummary(res.data);
    } catch {
      // Silent — banner is supplementary
    }
  }, []);

  const fetchConflictSummary = useCallback(async () => {
    try {
      const res = await api.get('/source-conflicts');
      setConflictSummary(res.data?.summary || null);
    } catch {
      // Silent — badge is supplementary
    }
  }, []);

  useEffect(() => {
    fetchAlertSummary();
    fetchConflictSummary();
    const interval = setInterval(() => {
      fetchAlertSummary();
      fetchConflictSummary();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchAlertSummary, fetchConflictSummary]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDismissBanner = () => setBannerDismissed(true);

  const topAlert = alertSummary?.top_alert;
  const totalUnack = alertSummary?.total_unacknowledged || 0;
  const showBanner = topAlert && !bannerDismissed && ['CRITICAL', 'HIGH'].includes(topAlert.severity);

  return (
    <div className="min-h-screen bg-[#09090B] flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#121214] border-r border-white/5 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/5">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold tracking-tight font-['Outfit']">
                  <span className="text-cyan-400">1OS</span> Sales Capture
                </h1>
                <p className="text-xs text-zinc-500 mt-1">Engine v1.0</p>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-1 text-zinc-400 hover:text-white"
                data-testid="close-sidebar-btn"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              const colors = accentColors[item.accent];

              // Conflict badge — only on Source Confidence
              const isConfidence = item.path === '/confidence';
              const highConflicts = conflictSummary?.high || 0;
              const totalConflicts = conflictSummary?.total || 0;
              const showConflictBadge = isConfidence && totalConflicts > 0;
              const badgeHref = highConflicts > 0
                ? '/confidence?severity=HIGH'
                : '/confidence';

              return (
                <Link
                  key={item.path}
                  to={isConfidence && showConflictBadge ? badgeHref : item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.path.replace('/', '') || 'overview'}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200 ${
                    isActive
                      ? `${colors} border`
                      : 'text-zinc-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon size={20} weight={isActive ? 'duotone' : 'regular'} />
                  <span className="text-sm font-medium flex-1">{item.label}</span>
                  {showConflictBadge && (
                    <span
                      data-testid="nav-conflict-badge"
                      title={`${totalConflicts} source conflict${totalConflicts === 1 ? '' : 's'} — ${highConflicts} HIGH, ${conflictSummary.medium} MEDIUM, ${conflictSummary.low} LOW`}
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md border ${
                        highConflicts > 0
                          ? 'bg-red-500/15 border-red-500/40 text-red-300'
                          : 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                      }`}
                    >
                      {highConflicts > 0
                        ? `${highConflicts} HIGH`
                        : totalConflicts}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-sm font-medium text-white">
                {(user?.user_name || user?.user_email || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.user_name || 'User'}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.user_email}</p>
              </div>
            </div>
            <div className="mt-2 space-y-1">
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-zinc-400 hover:text-white hover:bg-white/5 rounded-md transition-colors"
              >
                <SignOut size={18} />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 glass-elevated border-b border-white/5">
          <div className="flex items-center justify-between px-4 lg:px-6 h-14">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 text-zinc-400 hover:text-white"
                data-testid="open-sidebar-btn"
              >
                <List size={24} />
              </button>
              <div className="hidden sm:block">
                <h2 className="text-lg font-medium font-['Outfit'] tracking-tight">
                  {navItems.find(item => item.path === location.pathname)?.label || 'Sales Overview'}
                </h2>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/confidence')}
                data-testid="alert-bell-btn"
                className="relative p-2 text-zinc-400 hover:text-white hover:bg-white/5 rounded-md transition-colors"
              >
                <Bell size={20} />
                {totalUnack > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[9px] font-bold bg-red-500 text-white rounded-full">
                    {totalUnack > 9 ? '9+' : totalUnack}
                  </span>
                )}
              </button>
              <button
                data-testid="settings-btn"
                className="p-2 text-zinc-400 hover:text-white hover:bg-white/5 rounded-md transition-colors"
              >
                <Gear size={20} />
              </button>
            </div>
          </div>
        </header>

        {/* Global alert banner */}
        {showBanner && (
          <div className={`border-b px-4 lg:px-6 py-2 flex items-center gap-3 text-xs ${SEVERITY_STYLES[topAlert.severity]}`} data-testid="global-alert-banner">
            <Warning size={16} weight="fill" className="shrink-0" />
            <span className="flex-1 truncate">{topAlert.message}</span>
            <button
              onClick={() => navigate('/confidence')}
              className="shrink-0 px-2 py-0.5 rounded text-[10px] font-medium bg-white/5 hover:bg-white/10 transition-colors"
              data-testid="banner-view-btn"
            >
              View
            </button>
            <button onClick={handleDismissBanner} className="shrink-0 p-0.5 hover:bg-white/10 rounded transition-colors" data-testid="banner-dismiss-btn">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
